import { Button } from "@/components/ui/button";
import {
  type QueuedScan,
  compressPhoto,
  enqueueScan,
  flushQueue,
  listQueue,
  removeFromQueue,
} from "@/lib/scan-queue";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `/scan` — vyfotit doklad telefonem.
 *
 * The page is built for one hand in a shop: one big button, a preview,
 * send. The photo goes into the phone's queue first and only then to the
 * server, so it can be taken without a signal and leaves on its own once
 * there is one.
 *
 * The camera opens through `<input capture>`, not getUserMedia: the
 * native camera app does focus, flash and HDR better than anything we
 * would draw into a canvas, and people know it.
 */

export const Route = createFileRoute("/_app/scan")({ component: ScanPage });

function ScanPage() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ url: string; blob: Blob } | null>(null);
  const [note, setNote] = useState("");
  const [queue, setQueue] = useState<QueuedScan[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  const refreshQueue = useCallback(async () => {
    setQueue(await listQueue().catch(() => []));
  }, []);

  const send = useCallback(async () => {
    setBusy(true);
    try {
      const result = await flushQueue();
      if (result.sent > 0 && result.remaining === 0) {
        setStatus(`Sent (${result.sent}). Done.`);
      } else if (result.remaining > 0) {
        setStatus(
          result.sent > 0
            ? `Sent ${result.sent}, ${result.remaining} waiting.`
            : `Couldn't send yet — ${result.remaining} waiting. I'll try again once there is a signal.`,
        );
      }
    } finally {
      setBusy(false);
      await refreshQueue();
    }
  }, [refreshQueue]);

  // The queue is flushed when the page opens and whenever the phone gets
  // back on the network — nobody should have to think about it.
  useEffect(() => {
    void refreshQueue();
    const goOnline = () => {
      setOnline(true);
      void send();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) void send();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refreshQueue, send]);

  async function onPhotoPicked(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setBusy(true);
    setStatus(null);
    try {
      const blob = await compressPhoto(file);
      if (preview) URL.revokeObjectURL(preview.url);
      setPreview({ url: URL.createObjectURL(blob), blob });
    } catch {
      setStatus("Couldn't process the photo. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function keepPhoto() {
    if (!preview) return;
    setBusy(true);
    try {
      await enqueueScan({ blob: preview.blob, note: note.trim() });
      URL.revokeObjectURL(preview.url);
      setPreview(null);
      setNote("");
      setStatus("Saved. Sending…");
      await send();
    } catch {
      setStatus("Couldn't save it to the queue on this phone.");
    } finally {
      setBusy(false);
    }
  }

  function discardPhoto() {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  return (
    <div className="mx-auto w-full max-w-lg space-y-4 p-4">
      <div>
        <h1 className="font-semibold text-2xl">Scan a document</h1>
        <p className="text-muted-foreground text-sm">
          Photograph the whole receipt or invoice, straight on. We read the details ourselves — the
          accountant only confirms them.
        </p>
        {/* Inside the installed app there is nothing to download. */}
        {!window.matchMedia("(display-mode: standalone)").matches && (
          <a href="/app/hub.apk" className="mt-1 inline-block text-sm underline">
            Get the Android app
          </a>
        )}
      </div>

      {!online && (
        <p className="rounded-md bg-amber-50 p-3 text-amber-900 text-sm dark:bg-amber-950/40 dark:text-amber-200">
          The phone is offline. Keep shooting — documents wait in the queue and leave on their own.
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPhotoPicked}
      />

      {preview ? (
        <div className="space-y-3">
          <img
            src={preview.url}
            alt="Photographed document"
            className="w-full rounded-lg border object-contain"
          />
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (what it was for) — optional"
            className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
          />
          <div className="flex gap-2">
            <Button className="h-14 flex-1 text-base" disabled={busy} onClick={keepPhoto}>
              {busy ? "Saving…" : "Use"}
            </Button>
            <Button
              variant="outline"
              className="h-14 flex-1 text-base"
              disabled={busy}
              onClick={discardPhoto}
            >
              Retake
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className="h-24 w-full text-lg"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? "Processing…" : "Photograph a document"}
        </Button>
      )}

      {status && <p className="text-muted-foreground text-sm">{status}</p>}

      {queue.length > 0 && (
        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm">Waiting to be sent: {queue.length}</span>
            <Button variant="outline" size="sm" disabled={busy} onClick={send}>
              Zkusit znovu
            </Button>
          </div>
          <ul className="space-y-1 text-muted-foreground text-xs">
            {queue.map((scan) => (
              <li key={scan.id} className="flex items-center justify-between gap-2">
                <span>
                  {new Date(scan.capturedAt).toLocaleString("cs-CZ")}
                  {scan.attempts > 0 && ` · ${scan.attempts} failed attempt(s)`}
                  {scan.note && ` · ${scan.note}`}
                </span>
                <button
                  type="button"
                  className="underline"
                  onClick={async () => {
                    await removeFromQueue(scan.id);
                    await refreshQueue();
                  }}
                >
                  zahodit
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
